/**
 * Analyses list page - shows completed diligence runs
 */
import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2, FileText, ArrowRight, BarChart3, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import dataService, { AnalysisRecord } from '../services/data';
import api from '../services/api';
import { appendAuthToken } from '../services/auth';

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const recommendationLabels: Record<string, string> = {
  strong_yes: 'Strong Yes',
  deal: 'Deal',
  consider: 'Yes If',
  pass: 'Pass'
};

const badgeStyles: Record<string, string> = {
  strong_yes: 'bg-green-100 text-green-800 border-green-200',
  deal: 'bg-blue-100 text-blue-800 border-blue-200',
  consider: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pass: 'bg-gray-100 text-gray-700 border-gray-200'
};

const formatTier = (value?: string) =>
  value ? value.replace(/_/g, ' ').toUpperCase() : undefined;

const AnalysesPage: React.FC = () => {
  const [analyses, setAnalyses] = useState<AnalysisRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const apiBaseUrl = api.defaults.baseURL?.replace(/\/$/, '') || '';

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const result = await dataService.fetchAnalyses();
      if (result.error) {
        toast.error(result.error);
      } else {
        setAnalyses(result.data || []);
      }
      setIsLoading(false);
    };

    loadData();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analyses</h1>
            <p className="text-gray-600 mt-1">Track every diligence run and revisit the AI recommendation.</p>
          </div>
          <Link
            to="/new-analysis"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <FileText className="w-4 h-4 mr-2" />
            Start new analysis
          </Link>
        </div>

        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading analyses...
          </div>
        ) : analyses.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-600">
            <TrendingUp className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No analyses yet</h2>
            <p className="text-sm">Create your first company analysis to see it appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => {
              const recommendation = analysis.recommendation || 'consider';
              const label = recommendationLabels[recommendation] || recommendation;
              const badgeClass = badgeStyles[recommendation] || 'bg-gray-100 text-gray-700 border-gray-200';
              const metadata = analysis.reportMetadata;
              const derivedConfidence =
                analysis.confidence ?? (metadata ? Math.round(metadata.confidenceScore * 100) : undefined);
              const analystCount = metadata?.analystDomains?.length;
              const tierLabel = formatTier(analysis.decisionTier || metadata?.overallRecommendation);
              const monteCarloSummary = metadata?.monteCarloMetrics?.slice(0, 2).join(' • ');

              return (
                <div key={analysis.id} className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{analysis.companyName}</h2>
                      <p className="text-sm text-gray-600">
                        {[analysis.industry, analysis.fundingStage].filter(Boolean).join(' • ') || 'Stage TBD'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Submitted {formatDate(analysis.createdAt)}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${badgeClass}`}>
                        {label}
                      </div>
                      <div className="text-sm text-gray-600">
                        Overall score:{' '}
                        {typeof analysis.score === 'number' ? `${analysis.score}/100` : 'Pending'}
                      </div>
                      {tierLabel && (
                        <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
                          Decision tier: {tierLabel}
                        </div>
                      )}
                      {analysis.documents.length > 0 && (
                        <div className="text-xs text-gray-500">
                          {analysis.documents.length} supporting document{analysis.documents.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {analysis.summary && (
                    <p className="mt-4 text-sm text-gray-700 leading-relaxed">
                      {analysis.summary.length > 260 ? `${analysis.summary.slice(0, 260)}…` : analysis.summary}
                    </p>
                  )}

                  {metadata && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center space-x-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="text-xs text-blue-700 font-semibold">Weighted Confidence</div>
                          <div className="font-medium text-blue-900">{Math.round(metadata.confidenceScore * 100)}%</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
                        <span className="text-purple-600 text-xs font-semibold">Analyst Domains</span>
                        <span className="font-medium text-purple-900">{analystCount || 0}</span>
                      </div>
                      {monteCarloSummary && (
                        <div className="flex items-center space-x-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-green-800">
                          <span className="text-xs font-semibold uppercase tracking-wide">Monte Carlo</span>
                          <span className="text-sm font-medium truncate" title={metadata.monteCarloMetrics.join(', ')}>
                            {monteCarloSummary}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm">
                    <div className="text-gray-500">
                      Confidence {derivedConfidence !== undefined ? `${derivedConfidence}%` : 'n/a'}
                      {analystCount ? ` • ${analystCount} specialist analysts` : ''}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                      <Link
                        to={`/detailed-report/${analysis.id}`}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-200 text-blue-700 font-medium rounded-full hover:bg-blue-50 transition-colors"
                      >
                        Open detailed report
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                      <button
                        onClick={() => window.open(appendAuthToken(`${apiBaseUrl}/analyses/${analysis.id}/detailed-report.pdf`), '_blank', 'noopener')}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalysesPage;
